import {
  Button,
  Divider,
  HStack,
  Icon,
  IconButton,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import { BookText, Box, Clock, User2 } from "lucide-react";
import { useMemberAuthStore } from "modules/members/auth";
import { humanDuration } from "modules/utils";
import { FiArrowLeft, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { useParams } from "react-router-dom";
import { MF_ACCESS } from "state/members/access_v2/catalogs";
import PageSpinner from "ui/PageSpinner";

const ProvisioningTask = () => {
  const { id } = useParams();
  const request = MF_ACCESS.useGetProvisioningTask(id);
  const store = useMemberAuthStore();
  const { mutate: decide, isLoading } = MF_ACCESS.useDecideProvisioningTask(
    id as string,
  );
  const { isOpen: approveOpen, onToggle: toggleApprove } = useDisclosure();
  const { isOpen: denyOpen, onToggle: toggleDeny } = useDisclosure();
  const toast = useToast();

  const onDecide = (result: "completed" | "refused") => {
    request &&
      decide({
        resourceAccessId: request.id,
        result,
        actionAssigneeId: id!,
      }).then(() =>
        toast({
          status: "success",
          variant: "subtle",
          title: "Request " + result,
        }),
      );
  };

  if (!request && store.accessToken) {
    return (
      <VStack
        pb="200px"
        w="full"
        minH="100vh"
        bg="gray.50"
        pt={["50px", "100px"]}
        spacing={10}
      >
        <Text variant="xs.d.sb" textAlign="center">
          Request not found
        </Text>
        <Text variant="lil" textAlign="center">
          The request you're looking for no longer exists
        </Text>
      </VStack>
    );
  } else if (!store.accessToken) {
    return <PageSpinner />;
  }

  if (!request) {
    return null;
  }

  return (
    <VStack
      spacing={4}
      w="full"
      maxW="1200px"
      minH="100vh"
      h="100%"
      align="flex-start"
    >
      <VStack align="flex-start" spacing={1}>
        <HStack spacing={4}>
          <Text variant="lg.sb">Provision Access</Text>
        </HStack>
        <Text fontSize="sm" color="gray.600">
          {request.task.title}
        </Text>
      </VStack>
      <VStack
        bg="rgb(250, 250, 250)"
        borderRadius="5px"
        border="1px solid"
        borderColor="gray.200"
        align="flex-start"
        w="full"
        p={4}
        maxW={["full", "600px"]}
        spacing={6}
      >
        <VStack w="full" align="flex-start" spacing={1}>
          <HStack spacing={1}>
            <Icon as={BookText} fontSize="14px" />
            <Text variant="label">Request description</Text>
          </HStack>
          <Text variant="lil">{request.task.body}</Text>
        </VStack>
        <Divider />
        <HStack w="full" align="flex-start">
          <VStack w="50%" align="flex-start" spacing={1}>
            <HStack spacing={1}>
              <Icon as={User2} fontSize="14px" />
              <Text variant="label">Person to add</Text>
            </HStack>
            <Text variant="lil">{request.requester}</Text>
          </VStack>
          <VStack w="50%" align="flex-start" spacing={1}>
            <HStack spacing={1}>
              <Icon as={Box} fontSize="14px" />
              <Text variant="label">Resource</Text>
            </HStack>
            <Text variant="lil">{request.resource}</Text>
          </VStack>
        </HStack>
        <Divider />
        {request.task.decision === null ? (
          <HStack w="full" justify="flex-end">
            {!denyOpen && !approveOpen ? (
              <>
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiXCircle} />}
                  variant={"secondary"}
                  color="red.700"
                  onClick={toggleDeny}
                >
                  Refuse
                </Button>
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiCheckCircle} />}
                  variant={"secondary"}
                  color="green.700"
                  onClick={toggleApprove}
                >
                  Completed
                </Button>
              </>
            ) : denyOpen ? (
              <>
                <IconButton
                  variant={"secondary"}
                  size="sm"
                  icon={<Icon as={FiArrowLeft} />}
                  aria-label="back"
                  onClick={toggleDeny}
                />
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiXCircle} />}
                  colorScheme="red"
                  isLoading={isLoading}
                  onClick={() => onDecide("refused")}
                >
                  Confirm Refused
                </Button>
              </>
            ) : (
              <>
                <IconButton
                  variant={"secondary"}
                  size="sm"
                  icon={<Icon as={FiArrowLeft} />}
                  aria-label="back"
                  onClick={toggleApprove}
                />
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiCheckCircle} />}
                  colorScheme="green"
                  onClick={() => onDecide("completed")}
                  isLoading={isLoading}
                >
                  Confirm Complete
                </Button>
              </>
            )}
          </HStack>
        ) : (
          <HStack w="full" justify="flex-end">
            <Text variant="lil">
              This access has been <b>{request.task.decision}</b>{" "}
              {request.task.decidedAt &&
                formatDistanceToNow(new Date(request.task.decidedAt), {
                  addSuffix: true,
                })}
            </Text>
          </HStack>
        )}
      </VStack>
    </VStack>
  );
};

export default ProvisioningTask;
